import { type ChangeEvent, type JSX, useRef } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";

interface UploadAttachmentProps {
  onFileUpload: (file: File) => void;
  onFileUploadError: (file: File, errorMessage: string) => void;
  currentFileCount: number;
  renderButton: (onClick: () => void) => JSX.Element;
  allowedMimeTypes: string[];
  maxFiles: number;
  // max file size in bytes
  maxFileSize: number;
}

export default function UploadAttachment({
  onFileUpload,
  onFileUploadError,
  currentFileCount,
  renderButton,
  allowedMimeTypes,
  maxFiles,
  maxFileSize,
}: UploadAttachmentProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFileSelect(event: ChangeEvent<HTMLInputElement>) {
    const { files } = event.target;
    if (files) {
      for (let i = 0; i < files.length; i++) {
        const fileIndex = i + 1 + currentFileCount;

        const file = files.item(i);

        if (file.size > maxFileSize) {
          onFileUploadError(file, `Larger than ${maxFileSize / 1024 / 1024}MB`);
          continue;
        }
        const isAllowedType = allowedMimeTypes.includes(file.type);

        if (!isAllowedType) {
          onFileUploadError(file, "File type not supported");
          continue;
        }

        if (fileIndex > maxFiles) {
          onFileUploadError(file, "File limit exceeded");
          return;
        }

        onFileUpload(file);
      }
    }
    const input = event.target as HTMLInputElement;
    input.value = "";
  }

  const isLimitReached = currentFileCount >= maxFiles;

  return (
    <>
      {isLimitReached ? (
        <Tooltip>
          <TooltipTrigger asChild>
            {renderButton(() => fileInputRef.current?.click())}
          </TooltipTrigger>
          <TooltipContent>
            You&apos;ve added the maximum number of attachments ({maxFiles})
          </TooltipContent>
        </Tooltip>
      ) : (
        renderButton(() => fileInputRef.current?.click())
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        multiple
        onChange={handleFileSelect}
        accept={allowedMimeTypes.join(",")}
      />
    </>
  );
}
